import type { LucideIcon } from "lucide-react";
import { Fragment, type Key, type ReactNode } from "react";

import { Pager } from "@/components/ui/pager";
import { format } from "@/lib/format";
import { cn } from "@/lib/utils";

export function FinanceTimeline<T>({
	items,
	dateFor,
	itemKey,
	renderItem,
	page,
	pages,
	onPage,
}: {
	items: T[];
	dateFor: (item: T) => string;
	itemKey: (item: T) => Key;
	renderItem: (item: T) => ReactNode;
	page?: number;
	pages?: number;
	onPage?: (page: number) => void;
}) {
	const dates = [...new Set(items.map(dateFor))];

	return (
		<>
			<div className="mt-4 grid gap-5">
				{dates.map((date) => (
					<section key={date} className="grid gap-2 sm:grid-cols-[92px_minmax(0,1fr)]">
						<p className="font-mono text-xs font-semibold text-foreground sm:sticky sm:top-4 sm:self-start">
							{format.date(date)}
						</p>
						<div className="relative grid gap-2 border-l border-border pl-4 before:absolute before:-left-1 before:top-1 before:size-2 before:rounded-full before:bg-primary">
							{items
								.filter((item) => dateFor(item) === date)
								.map((item) => (
									<Fragment key={itemKey(item)}>{renderItem(item)}</Fragment>
								))}
						</div>
					</section>
				))}
			</div>
			{page !== undefined && pages !== undefined && onPage !== undefined && pages > 1 && (
				<div className="surface-panel mt-4 p-3">
					<Pager page={page} pages={pages} onPage={onPage} />
				</div>
			)}
		</>
	);
}

export function FinanceTimelineCard({
	icon: Icon,
	title,
	detail,
	note,
	amount,
	amountTone = "default",
	status,
	actions,
}: {
	icon: LucideIcon;
	title: string;
	detail: string;
	note?: string | null;
	amount: string;
	amountTone?: "default" | "positive";
	status?: ReactNode;
	actions?: ReactNode;
}) {
	return (
		<article className="surface-panel p-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex min-w-0 items-start gap-3">
					<Icon className="mt-0.5 size-4 shrink-0 text-primary" />
					<div className="min-w-0">
						<p className="break-words text-sm font-semibold text-foreground">{title}</p>
						<p className="mt-1 text-xs text-muted-foreground">{detail}</p>
						{note !== undefined && note !== null && (
							<p className="mt-1 break-words text-xs text-muted-foreground">{note}</p>
						)}
					</div>
				</div>
				<div className="flex shrink-0 flex-col items-end gap-2">
					<p
						className={cn(
							"font-mono text-sm font-semibold",
							amountTone === "default" && "text-foreground",
							amountTone === "positive" && "text-success",
						)}
					>
						{amount}
					</p>
					{status}
				</div>
			</div>
			{!!actions && (
				<div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-border pt-3">
					{actions}
				</div>
			)}
		</article>
	);
}
